import {StyleSheet} from "aphrodite";
import * as React from "react";

import {View} from "@khanacademy/wonder-blocks-core";
import Link from "@khanacademy/wonder-blocks-link";
import {
    withMarkConversion,
    type ConversionName,
    type WithMarkConversionProps,
    type WithoutMarkConversion,
} from "@khan/analytics-learning-time";
import {mediaQueries} from "@khan/shared-styles";
import {redirect} from "@khan/urls";

import Illustration from "./illustration.tsx";
import typographyStyles from "./typography/styles.ts";
import {
    SansSerifBodySmall,
    SerifHeadingSmall,
} from "./typography/typography.ts";

type OwnProps = {
    illustration: string;
    headline: string;
    copy: string;
    link?: {
        href: string;
        copy: string;
        external: boolean;
        openNewWindow?: boolean;
        conversion?: ConversionName;
    };
};

type Props = OwnProps & WithMarkConversionProps;

class ListItem extends React.Component<Props> {
    markConversion: (e: React.SyntheticEvent) => void = (
        e: React.SyntheticEvent,
    ) => {
        const {link, markConversion} = this.props;
        if (link) {
            const {external, conversion, openNewWindow, href} = link;

            if (conversion) {
                markConversion({id: conversion}).then(() => {
                    if (conversion && external && href) {
                        if (openNewWindow) {
                            const win = window.open(href, "_blank");
                            // @ts-expect-error - TS2531 - Object is possibly 'null'.
                            win.focus();
                        } else {
                            redirect(href);
                        }
                    }
                });

                if (external && href) {
                    e.preventDefault();
                }
            }
        }
    };

    render(): React.ReactNode {
        const {headline, copy, illustration, link} = this.props;
        return (
            <View style={styles.listItem}>
                <View style={styles.illustration}>
                    <Illustration name={illustration} />
                </View>
                <View style={styles.content}>
                    <SerifHeadingSmall tag="h3" style={styles.subHeader}>
                        {headline}
                    </SerifHeadingSmall>
                    <SansSerifBodySmall>{copy}</SansSerifBodySmall>
                    {link && (
                        <Link
                            style={[
                                typographyStyles.SansSerifBodySmall,
                                styles.link,
                            ]}
                            href={link.href}
                            target={link.openNewWindow ? "_blank" : undefined}
                            onClick={this.markConversion}
                        >
                            {link.copy + " ›"}
                        </Link>
                    )}
                </View>
            </View>
        );
    }
}

type ExportProps = WithoutMarkConversion<
    JSX.LibraryManagedAttributes<
        typeof ListItem,
        React.ComponentProps<typeof ListItem>
    >
>;
/**
 * TODO(somewhatabstract): This file is not properly annotated for learning
 * time, but we've decided it's not currently a priority.
 */
export default withMarkConversion(ListItem) as React.ComponentType<ExportProps>;

const styles = StyleSheet.create({
    illustration: {
        alignItems: "center",
        justifyContent: "center",
        height: 104,
        width: 106,
        alignSelf: "center",
        overflow: "visible",
        zIndex: -1,
        margin: "18px 0 6px 0",

        [mediaQueries.mdOrSmaller]: {
            flexShrink: 1,
        },
        [mediaQueries.md]: {
            marginRight: 42,
            marginTop: 34,
            marginBottom: 0,
            alignSelf: "start",
        },
        [mediaQueries.smOrSmaller]: {
            marginBottom: 12,
        },
    },
    listItem: {
        flexBasis: 0,
        flexGrow: 1,
        textAlign: "left",
        [mediaQueries.md]: {
            flexBasis: "auto",
            width: 504,
            flexDirection: "row",
            marginLeft: 0,
            marginRight: 0,
        },
        [mediaQueries.smOrSmaller]: {
            flexBasis: "auto",
            width: "auto",
        },
    },
    subHeader: {
        margin: "0 0 20px 0",
        [mediaQueries.smOrSmaller]: {
            marginBottom: 12,
        },
    },
    content: {
        textAlign: "center",

        [mediaQueries.md]: {
            maxWidth: 304,
            flexShrink: 1,
            textAlign: "left",
        },
    },
    link: {
        marginTop: 24,
        fontWeight: "bold",
    },
});
